import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { withStyles } from 'tss-react/mui';
import ReactEcharts from 'echarts-for-react';
import { green, orange, red, grey, blue } from '@mui/material/colors';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { S_PART } from '@lba-dev/package.local-globals/services';
import {
  IQG_STATS_CONFIG,
} from '@lba-dev/package.local-globals/iqg';

import api from '../../api';
import notifSystem from '../../notifSystem';
import { getIQGChartOptions } from './IQGUtils';
import StatsPanel from '../Stats/common/panel/StatsPanel';
import { setDialog } from '../../actions/dialog';

const styles = {
  root: {
    padding: 24,
  },
  selectorContainer: {
    marginBottom: 24,
  },
  iqgScore: {
    fontSize: '3rem',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  iqgGreen: {
    color: green[500],
  },
  iqgOrange: {
    color: orange[500],
  },
  iqgRed: {
    color: red[500],
  },
  statCard: {
    padding: 16,
    textAlign: 'center',
    borderRadius: 8,
    backgroundColor: grey[200],
  },
  statValue: {
    fontWeight: 'bold',
    color: blue[500],
  },
  statLabel: {
    fontSize: '0.875rem',
    color: grey[600],
    marginTop: 4,
  },
  savColor: {
    color: red[500],
    fontWeight: 'bold',
  },
  litigesColor: {
    color: orange[500],
    fontWeight: 'bold',
  },
  avisColor: {
    color: red[700],
    fontWeight: 'bold',
  },
  chartContainer: {
    marginTop: 32,
    height: 400,
  },
  emptyMessage: {
    textAlign: 'center',
    color: grey[500],
    padding: 32,
  },
};

const getScoreClass = (iqg, classes) => {
  if (iqg === null || iqg === undefined) {
    return '';
  }
  if (iqg >= 90) {
    return classes.iqgGreen;
  }
  if (iqg >= 80) {
    return classes.iqgOrange;
  }
  return classes.iqgRed;
};

const STYLE_KEYS = {
  interventions: 'statValue',
  sav: 'savColor',
  litiges: 'litigesColor',
  mauvaisAvis: 'avisColor',
};

const IQG_STATS = IQG_STATS_CONFIG.map((s) => ({
  ...s,
  styleKey: STYLE_KEYS[s.key],
}));

const openInterList = (stat, { chefId, iqgData }, dispatch) => {
  const from = dayjs(iqgData.periodeDebut).toDate();
  const to = dayjs(iqgData.periodeFin).toDate();
  api.stats
    .custom(`partenariat/zone/${chefId}/iqg/artisans`)
    .get({ from: from.toISOString(), to: to.toISOString() })
    .then((res) => {
      const { ids: artisanIds } = res.body().data();
      dispatch(setDialog({
        name: 'InterListDialog',
        open: true,
        hideClose: true,
        dialogProps: {
          title: stat.label,
          maxWidth: 'xl',
        },
        contentProps: {
          filter: {
            'date.ajout': { $gte: from, $lte: to },
            artisan: { $in: artisanIds },
            ...stat.extraFilter,
          },
          sort: { id: -1 },
          count: 100,
          displayName: 'decote',
        },
        actions: [
          {
            children: 'Fermer',
            color: 'secondary',
            onClick: (r, close) => close(),
          },
        ],
      }));
    })
    .catch(() =>
      notifSystem.error('Erreur', 'Impossible de charger les artisans')
    )
};

const openArtisanList = (chefId, iqgData, dispatch) => {
  const from = dayjs(iqgData.periodeDebut).toDate();
  const to = dayjs(iqgData.periodeFin).toDate();
  api.stats
    .custom(`partenariat/zone/${chefId}/iqg/artisans`)
    .get({ from: from.toISOString(), to: to.toISOString() })
    .then((res) => {
      const { ids: artisanIds } = res.body().data();
      dispatch(setDialog({
        name: 'ArtisanListDialog',
        open: true,
        hideClose: true,
        dialogProps: {
          title: 'Artisans actifs de la zone',
          maxWidth: 'xl',
        },
        contentProps: {
          filter: { _id: { $in: artisanIds } },
          sort: { _id: -1 },
          count: 50,
        },
        actions: [
          {
            children: 'Fermer',
            color: 'secondary',
            onClick: (r, close) => close(),
          },
        ],
      }));
    })
    .catch(() =>
      notifSystem.error('Erreur', 'Impossible de charger les artisans')
    );
};

const IQGZoneDashboard = ({ users, classes, dispatch }) => {
  const [selectedChef, setSelectedChef] = useState('');
  const [iqgData, setIqgData] = useState(null);
  const [historiqueData, setHistoriqueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFrom, setHistoryFrom] = useState(
    dayjs().startOf('year').toDate()
  );
  const [historyTo, setHistoryTo] = useState(dayjs().endOf('year').toDate());

  const chefsDeZone = users.filter(
    (u) =>
      u.service === S_PART &&
      u.act &&
      u.areas && u.areas.length > 0
  );

  useEffect(() => {
    if (!selectedChef) {
      setIqgData(null);
      return;
    }

    setLoading(true);

    api.stats
      .custom(`partenariat/zone/${selectedChef}/iqg`)
      .get()
      .then((res) => {
        setIqgData(res.body().data());
      })
      .catch(() => {
        notifSystem.error('Erreur', 'Impossible de charger IQG de la zone');
        setIqgData(null);
      })
      .finally(() => setLoading(false));
  }, [selectedChef]);

  useEffect(() => {
    if (!selectedChef) {
      setHistoriqueData([]);
      return;
    }

    setLoadingHistory(true);

    api.stats
      .custom(`partenariat/zone/${selectedChef}/iqg/historique`)
      .get({
        from: historyFrom.toISOString(),
        to: historyTo.toISOString(),
      })
      .then((res) => {
        setHistoriqueData(res.body().map((e) => e.data()));
      })
      .catch(() => {
        notifSystem.error('Erreur', 'Impossible de charger historique IQG');
        setHistoriqueData([]);
      })
      .finally(() => setLoadingHistory(false));
  }, [selectedChef, historyFrom, historyTo]);

  const periodeLabel = iqgData
    ? `De ${dayjs(iqgData.periodeDebut).format('MMM YYYY')} ` +
      `à ${dayjs(iqgData.periodeFin).format('MMM YYYY')}`
    : '';

  const handleHistoryDatesChange = (newFrom, newTo) => {
    setHistoryFrom(newFrom);
    setHistoryTo(newTo);
  };

  const historyAnneeLabel = dayjs(historyFrom).format('YYYY');


  return (
    <Box className={classes.root}>
      {/* Sélecteur Chef de zone */}
      <Box className={classes.selectorContainer}>
        <FormControl fullWidth>
          <InputLabel id="chef-de-zone-label">Chef de zone</InputLabel>
          <Select
            labelId="chef-de-zone-label"
            value={selectedChef}
            onChange={(e) => setSelectedChef(e.target.value)}
            label="Chef de zone"
          >
            {chefsDeZone.map((chef) => (
              <MenuItem key={chef._id} value={chef._id}>
                {chef.fullName || chef.login}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Bloc IQG Zone */}
      {selectedChef && (
        <>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            {loading && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}
            {!loading && iqgData && (
              <Grid container spacing={3}>
                {/* Score IQG */}
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      IQG Zone (12 derniers mois)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      gutterBottom
                    >
                      {periodeLabel}
                    </Typography>
                    <Typography
                      variant="h2"
                      className={`
                        ${classes.iqgScore}
                        ${getScoreClass(iqgData.iqg, classes)}
                      `}
                    >
                      {iqgData.iqg === null ? 'N/A' : `${iqgData.iqg}%`}
                    </Typography>
                    {iqgData.updatedAt && (
                      <Typography variant="caption" color="textSecondary">
                        Mis à jour le{' '}
                        {dayjs(iqgData.updatedAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Statistiques */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    {IQG_STATS.map((stat) => (
                      <Grid
                        item
                        xs={6}
                        sm={4}
                        key={stat.key}
                        style={{ cursor: 'pointer' }}
                        onClick={() =>
                          openInterList(
                            stat,
                            { chefId: selectedChef, iqgData },
                            dispatch
                          )
                        }
                      >
                        <Box className={classes.statCard}>
                          <Typography
                            className={classes[stat.styleKey]}
                          >
                            {iqgData[stat.dataKey]}
                          </Typography>
                          <Typography className={classes.statLabel}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                    <Grid
                      item
                      xs={6}
                      sm={4}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        openArtisanList(selectedChef, iqgData, dispatch)
                      }
                    >
                      <Box className={classes.statCard}>
                        <Typography className={classes.statValue}>
                          {iqgData.nbArtisans}
                        </Typography>
                        <Typography className={classes.statLabel}>
                          Artisans actifs
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {!loading && !iqgData && (
              <Box className={classes.emptyMessage}>
                Aucune donnée disponible pour cette zone
              </Box>
            )}
          </Paper>

          {/* Histogramme historique */}
          <StatsPanel
            title={`Historique IQG Zone ${historyAnneeLabel}`}
            onDatesChange={handleHistoryDatesChange}
            enabledModes={[false, false, false, true, false]}
            defaultState={{
              mode: 'year',
              from: historyFrom,
              to: historyTo,
            }}
            displayModePanel={true}
            displayDatesDropper={true}
          >
            {loadingHistory && (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            )}
            {!loadingHistory && historiqueData.length > 0 && (
              <Box className={classes.chartContainer}>
                <ReactEcharts
                  option={getIQGChartOptions(historiqueData, {
                    showNbArtisans: true,
                  })}
                  style={{ height: '100%', width: '100%' }}
                />
              </Box>
            )}
            {!loadingHistory && historiqueData.length === 0 && (
              <Box className={classes.emptyMessage}>
                Aucune donnée historique disponible pour cette période
              </Box>
            )}
          </StatsPanel>
        </>
      )}

      {!selectedChef && (
        <Box className={classes.emptyMessage}>
          <Typography variant="h6" color="textSecondary">
            Sélectionnez un chef de zone pour afficher l'IQG de sa zone
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch) => ({ dispatch });

const mapStateToProps = ({ users }) => ({
  users,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(IQGZoneDashboard, styles));
